/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { usePathname, useRouter, useTranslations } from "@i18n/next";
import {
  HallowAnalyticsEvent,
  useAnalytics,
  useViewedScreen,
} from "@packages/analytics";
import { SwitchProfiles } from "@packages/kids";
import {
  Button,
  CloseIcon,
  createModal,
  cssMediaQueryMinWidthTablet,
  IconButton,
  Modal,
  numericPercentages,
  numericPixels,
  radius,
  showModal,
  spacing,
  Text,
  type ThemeType,
  useModal,
} from "@packages/ui";

export type AccessRestrictedModalProps = {
  wouldSwitchingProfilesHelp: boolean;
};

const MODAL_NAME = "access_restricted";
const OKAY_KEY = "general_word_okay";
const SWITCH_PROFILES_KEY = "general_words_switch_profiles";

const contentCSS: Interpolation<ThemeType> = (theme) => ({
  alignItems: "stretch",
  backgroundColor: theme.semanticColors.onOverlayBackground,
  borderRadius: radius.l,
  display: "flex",
  flexDirection: "column",
  gap: spacing.l,
  paddingBlock: spacing.l,
  paddingInline: 0,
  width: numericPercentages[100],
  [cssMediaQueryMinWidthTablet]: {
    maxWidth: numericPixels[480],
    paddingBlock: spacing.xl,
    paddingInline: spacing.xl,
  },
});

const mobileInfoCSS: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
  paddingInline: spacing.ms,
  textAlign: "center",
  [cssMediaQueryMinWidthTablet]: {
    display: "none",
  },
};

const desktopHeaderCSS: Interpolation = {
  alignItems: "center",
  display: "none",
  gap: spacing.ms,
  width: numericPercentages[100],
  [cssMediaQueryMinWidthTablet]: {
    display: "flex",
  },
};

const desktopTitleCSS: Interpolation = {
  flex: 1,
};

const desktopBodyCSS: Interpolation = {
  display: "none",
  [cssMediaQueryMinWidthTablet]: {
    display: "block",
  },
};

const ctaCSS: Interpolation = {
  alignItems: "stretch",
  display: "flex",
  flexDirection: "column",
  gap: spacing.ms,
  paddingInline: spacing.l,
  width: numericPercentages[100],
  [cssMediaQueryMinWidthTablet]: {
    gap: spacing.s,
    paddingInline: 0,
  },
};

const mobileSubtitleCSS: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHighest,
  marginTop: spacing.xs,
  textAlign: "center",
});

export const AccessRestrictedModal = createModal<AccessRestrictedModalProps>(
  ({ wouldSwitchingProfilesHelp }) => {
    const modal = useModal();
    const pathname = usePathname();
    const router = useRouter();
    const { track } = useAnalytics();
    const t = useTranslations();
    const okayText = t(OKAY_KEY);
    const switchProfilesText = t(SWITCH_PROFILES_KEY);

    useViewedScreen({
      event: HallowAnalyticsEvent.ViewedModal,
      isReady: modal.visible,
      properties: {
        modal_name: MODAL_NAME,
      },
      screen: pathname,
    });

    const handleOpenChange = (open: boolean) => {
      if (open) modal.show();
      else {
        modal.resolve(false);
        modal.remove();
      }
    };

    const handleClose = () => {
      modal.resolve(false);
      modal.remove();
      router.push("/home");
    };

    const handleOkay = () => {
      track({
        event: HallowAnalyticsEvent.TappedModalPrimaryCTA,
        properties: {
          cta_lokalise_key: OKAY_KEY,
          cta_text: okayText,
          modal_name: MODAL_NAME,
          screen_name: pathname,
        },
      });
      modal.resolve(true);
      modal.remove();
      router.push("/home");
    };

    const handleSwitchProfiles = () => {
      track({
        event: HallowAnalyticsEvent.TappedModalSecondaryCTA,
        properties: {
          cta_lokalise_key: SWITCH_PROFILES_KEY,
          cta_text: switchProfilesText,
          modal_name: MODAL_NAME,
          screen_name: pathname,
        },
      });
      modal.resolve(true);
      modal.remove();
      showModal(SwitchProfiles, {});
    };

    return (
      <Modal
        contentCSS={contentCSS}
        onOpenChange={handleOpenChange}
        open={modal.visible}
        title={t("access_restricted_alert_title")}
        contentProps={{
          onEscapeKeyDown: (e) => e.preventDefault(),
          onInteractOutside: (e) => e.preventDefault(),
        }}
      >
        <div css={mobileInfoCSS}>
          <Text as="h2" size="l" type="title">
            {t("access_restricted_alert_title")}
          </Text>
          <Text as="p" size="m" type="body" css={mobileSubtitleCSS}>
            {t("access_restricted_alert_message")}
          </Text>
        </div>

        <div css={desktopHeaderCSS}>
          <Text as="h2" css={desktopTitleCSS} size="s" type="headline">
            {t("access_restricted_alert_title")}
          </Text>
          <IconButton
            icon={<CloseIcon />}
            onClick={handleClose}
            size="s"
            variant="transparentPrimary"
          />
        </div>
        <Text as="p" css={desktopBodyCSS} size="m" type="body">
          {t("access_restricted_alert_message")}
        </Text>

        <div css={ctaCSS}>
          <Button onClick={handleOkay} isFullWidth size="l">
            {okayText}
          </Button>
          {wouldSwitchingProfilesHelp && (
            <Button
              onClick={handleSwitchProfiles}
              variant="transparentPrimary"
              isFullWidth
              size="l"
            >
              {switchProfilesText}
            </Button>
          )}
        </div>
      </Modal>
    );
  },
);
